import { ReactNode } from 'react';
import { BaseIssue } from 'valibot';

import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Autocomplete,
	AutocompleteRenderGroupParams,
	Chip,
	FormHelperText,
	InputAdornment,
	OutlinedInput,
} from '@mui/material';

import { useFieldContext } from '#form';

import { LabelInput } from '@repo/ui/LabelInput';

type SelectProps<T> = {
	label: string;
	options: Array<T>;
	isLoading?: boolean;
	fullWidth?: true;
	disabled?: boolean;
	optional?: boolean;
	readOnly?: boolean;
	getOptionLabel: (option: T) => string;
	getChipLabel?: (option: T) => string;
	renderOption?: (option: T) => ReactNode;
	onChipClick?: (option: T) => void;
	groupBy?: (option: T) => string;
	renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
};

export function SingleAutocompleteInput<T extends object | string>(
	props: SelectProps<T>,
) {
	const field = useFieldContext<T | null>();
	return <AutocompleteInput {...props} multiple={false} field={field} />;
}

export function MultiAutocompleteInput<T extends object | string>(
	props: SelectProps<T>,
) {
	const field = useFieldContext<T[] | null>();
	return <AutocompleteInput {...props} multiple field={field} />;
}

function AutocompleteInput<T extends object | string>({
	field,
	multiple,
	label,
	options,
	getOptionLabel,
	getChipLabel = getOptionLabel,
	renderOption,
	isLoading = false,
	fullWidth,
	disabled,
	optional,
	readOnly,
	onChipClick,
	groupBy,
	renderGroup,
}: SelectProps<T> &
	(
		| { multiple: false; field: ReturnType<typeof useFieldContext<T | null>> }
		| { multiple: true; field: ReturnType<typeof useFieldContext<T[] | null>> }
	)) {
	const error = (
		field.state.meta as {
			errors?: Array<string | BaseIssue<unknown>>;
		}
	).errors?.[0];

	return (
		<Autocomplete
			fullWidth={fullWidth}
			multiple={multiple}
			disabled={disabled}
			forcePopupIcon={readOnly ? false : undefined}
			id={field.name}
			value={field.state.value ?? null}
			onBlur={field.handleBlur}
			onChange={(_, newValue) => {
				// sigh, typescript, sigh
				if (multiple) {
					field.handleChange(newValue as T[] | null);
				} else {
					field.handleChange(newValue as T | null);
				}
			}}
			options={options}
			getOptionLabel={getOptionLabel}
			groupBy={groupBy && ((v) => groupBy(v))}
			renderGroup={renderGroup}
			// eslint-disable-next-line @typescript-eslint/no-deprecated
			renderTags={(value, getTagProps) =>
				value.map((v, index) => {
					const { key, ...tagProps } = getTagProps({ index });
					return (
						<Chip
							key={key}
							size="small"
							label={getChipLabel(v)}
							{...tagProps}
							onClick={() => {
								onChipClick?.(v);
							}}
						/>
					);
				})
			}
			renderOption={
				renderOption ?
					({ key, ...rest }, option) => (
						<li key={key} {...rest}>
							{renderOption(option)}
						</li>
					)
				:	undefined
			}
			renderInput={(params) => (
				<LabelInput
					label={label}
					fieldName={field.name}
					labelEnd={optional ? <LabelInput.Optional /> : undefined}
				>
					<OutlinedInput
						size="small"
						sx={{ mt: 1 }}
						{...params.InputProps}
						inputProps={{
							...params.inputProps,
							autoComplete: 'new-password', // disable autocomplete and autofill
							readOnly: readOnly,
						}}
						endAdornment={
							<>
								<InputAdornment position="end">
									{(isLoading || field.state.meta.isValidating) && (
										<FontAwesomeIcon icon={faSpinner} spin />
									)}
								</InputAdornment>
								{params.InputProps.endAdornment}
							</>
						}
						error={field.state.meta.isTouched && error != null}
					/>
					{field.state.meta.isTouched && error != null && (
						<FormHelperText error>
							{typeof error === 'string' ? error : error.message}
						</FormHelperText>
					)}
				</LabelInput>
			)}
			slotProps={{
				listbox: {
					sx: {
						paddingTop: 0,
					},
				},
				popper: {
					sx: {
						display: readOnly ? 'none' : 'block',
					},
				},
			}}
		/>
	);
}
